import Card from "../Card";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAccounts } from "../../hooks/useAccounts";
import { useUnclaimedLbUsernames } from "../../hooks/useUnclaimedLbUsernames";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Input } from "../ui/Input";
import { Button, buttonVariants } from "../ui/Button";
import { Modal, ModalBody, ModalHeader } from "../Modal";
import { cn } from "../../lib/utils";
import Spinner from "../Spinner";
import type { AccountRole, AccountUpdateRequest, AccountView } from "../../types";

const ROLE_OPTIONS: { value: AccountRole; label: string }[] = [
  { value: "member", label: "Member" },
  { value: "editor", label: "Editor" },
  { value: "admin", label: "Admin" },
];
import { Notification } from "../ui/Notification";

// UX-only pre-check. Server is the source of truth — keep in sync with
// src/server/lib/lbusername.ts.
const LBUSERNAME_FORMAT = /^[a-z0-9_-]{2,15}$/;

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

const UserAdmin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const { show: showToast } = useToast();
  const [editing, setEditing] = useState<AccountView | null>(null);

  const isAdmin = user?.role === "admin";

  const { data: accounts, loading, error, update, remove } = useAccounts();

  // Wait for /me to resolve before judging the gate — otherwise an admin sees
  // a flash of "Access denied" while the identity round-trip is in flight.
  if (authLoading) {
    return (
      <Card className="text-center py-12">
        <Spinner />
      </Card>
    );
  }

  if (!isAdmin) {
    // Role is sourced from the /me response. The server enforces the real gate
    // (authorizeAdmin middleware), so this is a UX guard, not a security one.
    return (
      <Card className="border rounded-lg border-letterboxd-error-surface/60 bg-letterboxd-error-surface/20 text-letterboxd-text-primary">
        <p className="font-semibold text-letterboxd-error">Access denied</p>
        <p className="text-letterboxd-text-primary text-sm mt-1">
          This page is only available to admin accounts.
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate("/dashboard")}
          className="mt-4"
        >
          Back to dashboard
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/dashboard"
            className={cn(buttonVariants({ variant: "ghost" }), "p-0 text-sm mb-2")}
          >
            ← Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-letterboxd-text-primary">
            User management
          </h1>
          <p className="text-letterboxd-text-secondary text-sm mt-1">
            Edit account details and link Letterboxd.com usernames to accounts.
          </p>
        </div>
      </div>

      {error && <Notification status={{ type: "error", message: error }} />}

      {loading ? (
        <Card className="text-center py-12">
          <Spinner />
        </Card>
      ) : accounts.length === 0 && !error ? (
        <Card className="text-center py-12">
          <p className="text-letterboxd-text-secondary">No accounts found.</p>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-letterboxd-border">
                <th scope="col" className="py-3 px-4">
                  <span className="sr-only">Row number</span>
                </th>
                <th
                  scope="col"
                  className="py-3 px-4 text-letterboxd-text-secondary font-medium"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="py-3 px-4 text-letterboxd-text-secondary font-medium"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="py-3 px-4 text-letterboxd-text-secondary font-medium"
                >
                  Role
                </th>
                <th
                  scope="col"
                  className="py-3 px-4 text-letterboxd-text-secondary font-medium"
                >
                  Letterboxd
                </th>
                <th
                  scope="col"
                  className="py-3 px-4 text-letterboxd-text-secondary font-medium"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="py-3 px-4 text-letterboxd-text-secondary font-medium"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a, i) => (
                <tr key={a.id} className="border-b border-letterboxd-border/50">
                  <td className="py-3 px-4 text-letterboxd-text-secondary">
                    {i + 1}
                  </td>
                  <td className="py-3 px-4 text-letterboxd-text-primary">
                    {a.email ?? "—"}
                  </td>
                  <td className="py-3 px-4 text-letterboxd-text-primary">
                    {a.name ?? "—"}
                  </td>
                  <td className="py-3 px-4 text-letterboxd-text-primary capitalize">
                    {a.role}
                  </td>
                  <td className="py-3 px-4 text-letterboxd-text-primary">
                    {a.lbusername ? (
                      <a
                        href={`https://letterboxd.com/${a.lbusername}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-letterboxd-accent hover:underline"
                      >
                        {a.lbusername}
                      </a>
                    ) : (
                      <span className="text-letterboxd-text-muted">
                        (unlinked)
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-letterboxd-text-primary">
                    {formatDate(a.createdAt)}
                  </td>
                  <td className="py-3 px-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setEditing(a)}
                      className="text-sm"
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editing && (
        <EditAccountModal
          account={editing}
          accounts={accounts}
          isSelf={user?.id === editing.id}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            const result = await update(editing.id, patch);
            setEditing(null);
            if (result.requiresReauth) {
              // Admin changed their own email; their JWT rotated, so force a
              // re-login before continuing.
              logout();
              navigate("/login");
            }
          }}
          onDelete={async () => {
            await remove(editing.id);
            setEditing(null);
            showToast({ tone: "success", message: "Account deleted." });
          }}
        />
      )}
    </div>
  );
};

interface EditAccountModalProps {
  account: AccountView;
  accounts: AccountView[];
  isSelf: boolean;
  onClose: () => void;
  onSave: (patch: AccountUpdateRequest) => Promise<void>;
  onDelete: () => Promise<void>;
}

const EditAccountModal = ({
  account,
  accounts,
  isSelf,
  onClose,
  onSave,
  onDelete,
}: EditAccountModalProps) => {
  const [email, setEmail] = useState(account.email ?? "");
  const [name, setName] = useState(account.name ?? "");
  const [role, setRole] = useState<AccountRole>(account.role);
  const [lbusername, setLbusername] = useState(account.lbusername ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: unclaimed, loading: lbLoading } = useUnclaimedLbUsernames(
    accounts,
    account.lbusername,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Build a minimal patch — only include fields that actually changed, so
    // an unchanged email isn't sent as a redundant Supabase admin call.
    const patch: AccountUpdateRequest = {};
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    const trimmedLb = lbusername.trim().toLowerCase();

    if (trimmedEmail !== (account.email ?? "")) patch.email = trimmedEmail;
    if (trimmedName !== (account.name ?? "")) patch.name = trimmedName;
    if (role !== account.role) patch.role = role;
    if (trimmedLb !== (account.lbusername ?? "")) {
      patch.lbusername = trimmedLb.length === 0 ? null : trimmedLb;
    }

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    // UX-only format pre-check before round-trip.
    if (
      patch.lbusername !== undefined &&
      patch.lbusername !== null &&
      !LBUSERNAME_FORMAT.test(patch.lbusername)
    ) {
      setError(
        "Letterboxd.com usernames must be 2–15 characters; letters, numbers, hyphens, underscores only.",
      );
      return;
    }

    try {
      setSubmitting(true);
      await onSave(patch);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Update failed";
      // The server returns "This Letterboxd.com username has already been
      // claimed." on 409 — surface it inline rather than as a generic error.
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    try {
      setSubmitting(true);
      await onDelete();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose}>
      <ModalHeader onClose={onClose}>Edit account</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="edit-email"
              className="block text-sm font-medium text-letterboxd-text-secondary mb-2"
            >
              Email
            </label>
            <Input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              className="w-full"
            />
            {isSelf && email.trim() !== (account.email ?? "") && (
              <p className="text-xs text-letterboxd-text-muted mt-1">
                Changing your own email will sign you out — you'll need to log
                in again after saving.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="edit-name"
              className="block text-sm font-medium text-letterboxd-text-secondary mb-2"
            >
              Name
            </label>
            <Input
              id="edit-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              className="w-full"
            />
          </div>

          <div>
            <label
              htmlFor="edit-role"
              className="block text-sm font-medium text-letterboxd-text-secondary mb-2"
            >
              Role
            </label>
            <select
              id="edit-role"
              value={role}
              onChange={(e) => setRole(e.target.value as AccountRole)}
              disabled={submitting || isSelf}
              aria-describedby={isSelf ? "edit-role-help" : undefined}
              className="input-field w-full"
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {isSelf && (
              <p
                id="edit-role-help"
                className="text-xs text-letterboxd-text-muted mt-1"
              >
                You can't change your own role. Another admin must do it.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="edit-lbusername"
              className="block text-sm font-medium text-letterboxd-text-secondary mb-2"
            >
              Letterboxd username
            </label>
            <Input
              id="edit-lbusername"
              type="text"
              value={lbusername}
              onChange={(e) => setLbusername(e.target.value)}
              list="unclaimed-lb-usernames"
              placeholder={lbLoading ? "Loading suggestions…" : "(unlinked)"}
              autoComplete="off"
              disabled={submitting}
              className="w-full"
            />
            <datalist id="unclaimed-lb-usernames">
              {unclaimed.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </datalist>
            <p className="text-xs text-letterboxd-text-muted mt-1">
              Leave blank to unlink. Suggestions are unclaimed Letterboxd
              usernames known to the database.
            </p>
          </div>

          {error && <Notification status={{ type: "error", message: error }} />}

          <div className="flex justify-between items-center gap-2 pt-2 border-t border-letterboxd-border">
            {!confirmingDelete ? (
              <Button
                type="button"
                variant="link"
                onClick={() => setConfirmingDelete(true)}
                disabled={submitting || isSelf}
                className="text-sm text-letterboxd-error"
                title={
                  isSelf
                    ? "You cannot delete your own account here. Use the Supabase dashboard."
                    : "Delete account"
                }
              >
                Delete account
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-letterboxd-text-primary">Sure?</span>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  loading={submitting}
                >
                  Yes, delete
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={submitting}
                >
                  No
                </Button>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default UserAdmin;
